/**
 * CCC collection browser — companion script to the native filters block.
 *
 * Filtering, sorting, facet values and facet counts are Shopify's: the native
 * filters block (blocks/filters.liquid) renders them from collection.filters,
 * and assets/facets.js owns filter state, the URL and the AJAX section
 * re-render. This file adds what the block doesn't do:
 *
 *   - toolbar surgery: the search box moves into the block's control bar so
 *     search / count / sort share the CCC-style bar, and the sort label moves
 *     out of the pill;
 *   - load more + auto-load on approach (IntersectionObserver);
 *   - search over the whole collection via ?view=search-index.
 *
 * CRITICAL: facets.js REPLACES the section's DOM on every filter or sort
 * change, throwing away moved nodes and event listeners. Everything here is
 * therefore idempotent (guarded by data-ccc-wired flags on the new elements)
 * and re-applied by a MutationObserver watching the section container.
 */
(() => {
  const SEARCH_DEBOUNCE_MS = 150;
  const SEARCH_MAX_RESULTS = 120;

  /* ---- toolbar surgery --------------------------------------------------- */

  function wireToolbar(root) {
    const controlBar = root.querySelector('.facets-controls-wrapper');
    if (!controlBar) return;
    controlBar.classList.add('ccc-collection__controlbar');

    const searchBox = root.querySelector('.ccc-collection__search');
    if (searchBox && !controlBar.contains(searchBox)) {
      controlBar.prepend(searchBox);
      const statusEl = root.querySelector('[data-ccc-search-status]');
      if (statusEl) searchBox.after(statusEl);
    }

    // The toolbar is empty once its contents move into the control bar; if it
    // kept its box it would draw a stray line over dead space.
    const toolbar = root.querySelector('.ccc-collection__toolbar');
    if (toolbar && !toolbar.querySelector('*')) toolbar.hidden = true;

    // CCC's sort reads: plain "Sort:" label OUTSIDE a pill that holds the
    // current choice. The block renders the label inside the clickable
    // summary; move it just before the <details> so the pill is value+caret.
    const sorting = controlBar.querySelector('sorting-filter-component');
    const details = sorting?.querySelector('details');
    const label = details?.querySelector('summary .facets__label');
    if (details && label) {
      label.classList.add('ccc-collection__sort-label');
      details.before(label);
    }
  }

  /* ---- load more + auto-load --------------------------------------------- */

  async function loadMore(root, button) {
    if (button.dataset.loading || !button.dataset.nextUrl) return;
    button.dataset.loading = '1';
    button.disabled = true;
    const restLabel = button.textContent;
    if (button.dataset.loadingLabel) button.textContent = button.dataset.loadingLabel;

    const url = button.dataset.nextUrl;
    const separator = url.includes('?') ? '&' : '?';
    try {
      // ?view=load-more renders templates/collection.load-more.json — just the
      // card grid, a fraction of a full section render. paginate.next.url
      // already carries the active filter params. If the template were ever
      // missing, Shopify falls back to the default template, whose full page
      // still contains the same grid markup — heavier but not broken.
      const response = await fetch(url.includes('view=load-more') ? url : `${url}${separator}view=load-more`);
      if (!response.ok) throw new Error(`Load more failed: ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');

      // Re-query the grid at append time: a facets re-render may have swapped
      // the DOM since this handler was bound.
      const grid = root.querySelector('[data-ccc-grid]');
      if (!grid) return;
      const fragment = document.createDocumentFragment();
      doc.querySelectorAll('[data-ccc-grid] [data-ccc-card]').forEach((card) => fragment.appendChild(card));
      grid.appendChild(fragment);

      const nextButton = doc.querySelector('[data-ccc-load-more]');
      if (nextButton && nextButton.dataset.nextUrl) {
        button.dataset.nextUrl = nextButton.dataset.nextUrl;
        button.disabled = false;
        button.textContent = restLabel;
      } else {
        button.closest('.ccc-collection__load-more-wrap')?.remove();
      }
    } catch (error) {
      button.disabled = false;
      button.textContent = restLabel;
    } finally {
      delete button.dataset.loading;
    }
  }

  function wireLoadMore(root) {
    root.querySelectorAll('[data-ccc-load-more]').forEach((button) => {
      if (button.dataset.cccWired) return;
      button.dataset.cccWired = '1';
      button.addEventListener('click', () => loadMore(root, button));
    });

    // Auto-load as the shopper nears the bottom; the button is the fallback.
    // The wrapper disappears with the last page, ending the chain, and the
    // loading guard absorbs repeated firings. While search results are open
    // the grid is hidden, so the observer goes quiet.
    const wrap = root.querySelector('.ccc-collection__load-more-wrap');
    if (wrap && !wrap.dataset.cccWired && 'IntersectionObserver' in window) {
      wrap.dataset.cccWired = '1';
      new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          const button = root.querySelector('[data-ccc-load-more]');
          if (button) loadMore(root, button);
        },
        { rootMargin: '900px 0px' }
      ).observe(wrap);
    }
  }

  /* ---- search over the whole collection ---------------------------------- */

  function wireSearch(root) {
    const results = root.querySelector('[data-ccc-search-results]');
    if (!results) return;

    let index = null;
    let indexPromise = null;
    let searchDebounceId = 0;

    function indexUrl(page) {
      const params = new URLSearchParams(window.location.search);
      params.delete('page');
      params.delete('section_id');
      params.set('view', 'search-index');
      if (page > 1) params.set('page', String(page));
      return `${window.location.pathname}?${params.toString()}`;
    }

    async function loadIndex() {
      if (index) return index;
      if (indexPromise) return indexPromise;
      indexPromise = (async () => {
        const first = await (await fetch(indexUrl(1))).json();
        let items = first.items;
        if (first.pages > 1) {
          const rest = await Promise.all(
            Array.from({ length: first.pages - 1 }, (_, i) =>
              fetch(indexUrl(i + 2)).then((r) => r.json())
            )
          );
          items = items.concat(...rest.map((r) => r.items));
        }
        index = items.map((it) => ({ ...it, q: it.t.toLowerCase() }));
        return index;
      })();
      return indexPromise;
    }

    function renderResults(query) {
      const main = root.querySelector('.ccc-collection__main');
      const status = root.querySelector('[data-ccc-search-status]');
      const q = query.trim().toLowerCase();
      if (!q) {
        results.hidden = true;
        results.replaceChildren();
        if (main) main.hidden = false;
        if (status) status.textContent = '';
        return;
      }
      if (!index) return;

      const matches = index.filter((it) => it.q.includes(q));
      const shown = matches.slice(0, SEARCH_MAX_RESULTS);

      const frag = document.createDocumentFragment();
      shown.forEach((it) => {
        const li = document.createElement('li');
        li.className = 'ccc-collection__item ccc-collection__result';
        const a = document.createElement('a');
        a.className = 'ccc-collection__result-link';
        a.href = it.u;
        if (it.i) {
          const img = document.createElement('img');
          img.className = 'ccc-collection__result-image';
          img.src = it.i;
          img.alt = '';
          img.loading = 'lazy';
          a.appendChild(img);
        }
        const body = document.createElement('span');
        body.className = 'ccc-collection__result-body';
        const title = document.createElement('span');
        title.className = 'ccc-collection__result-title';
        title.textContent = it.t;
        const price = document.createElement('span');
        price.className = 'ccc-collection__result-price';
        // st: 1 = preorder, 2 = sold out (absent = in stock)
        let caption = it.p;
        if (it.st === 1) caption = `${it.p} — ${root.dataset.labelPreorder || 'Preorder'}`;
        else if (it.st === 2) caption = `${it.p} — ${root.dataset.labelSoldOut || 'Sold out'}`;
        price.textContent = caption;
        body.append(title, price);
        a.appendChild(body);
        li.appendChild(a);
        frag.appendChild(li);
      });

      results.replaceChildren(frag);
      results.hidden = false;
      if (main) main.hidden = true;
      if (status) {
        const tpl =
          matches.length === 1
            ? root.dataset.labelSearchOne || '{count} match'
            : root.dataset.labelSearchMany || '{count} matches';
        let text = tpl.replace('{count}', String(matches.length));
        if (matches.length > shown.length) text += ` (showing first ${shown.length})`;
        status.textContent = text;
      }
    }

    root.querySelectorAll('[data-ccc-search]').forEach((search) => {
      if (search.dataset.cccWired) return;
      search.dataset.cccWired = '1';

      const warm = () => loadIndex().then(() => { if (search.value) renderResults(search.value); });
      search.addEventListener('focus', warm, { once: true });

      search.addEventListener('input', () => {
        window.clearTimeout(searchDebounceId);
        searchDebounceId = window.setTimeout(async () => {
          await loadIndex();
          renderResults(search.value);
        }, SEARCH_DEBOUNCE_MS);
      });

      search.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') event.preventDefault();
        if (event.key === 'Escape' && search.value) {
          event.preventDefault();
          search.value = '';
          window.clearTimeout(searchDebounceId);
          renderResults('');
        }
      });
    });
  }

  /* ---- wiring ------------------------------------------------------------ */

  function wire(root) {
    wireToolbar(root);
    wireLoadMore(root);
    wireSearch(root);
  }

  function init() {
    document.querySelectorAll('[data-ccc-collection]').forEach((root) => {
      wire(root);

      // facets.js re-renders the section on every filter/sort interaction,
      // replacing the DOM this script just wired. Watch the section container
      // and re-wire whatever appears; the data-ccc-wired guards make this
      // safe to run repeatedly.
      const container = root.closest('.shopify-section') || root.parentElement;
      if (!container || container.dataset.cccObserved) return;
      container.dataset.cccObserved = '1';
      let debounceId = 0;
      new MutationObserver(() => {
        window.clearTimeout(debounceId);
        debounceId = window.setTimeout(() => {
          container.querySelectorAll('[data-ccc-collection]').forEach(wire);
        }, 80);
      }).observe(container, { childList: true, subtree: true });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
